// ============================================================
// dashboardWidgets.ts — the Dashboard widget catalog + layout normalizer,
// shared by BOTH clients (frontend/ and mobile/).
//
// It lived in frontend/src/constants/dashboardWidgets.js until iOS started
// reading the same per-user layout (GET /api/dashboard/layout). Two copies of
// "which widgets exist and what a saved layout means" is how an iPhone and a
// browser come to disagree about the same person's dashboard, so the web file
// now re-exports this one.
//
// The user's ordering/visibility/width choices are stored per-user on the API
// (dbo.UserDashboardLayouts, sql/146) and reconciled against this catalog by
// `normalizeLayout` on every read. Adding a widget = one entry here + a render
// branch in each client that supports it (a client that does not simply skips
// the id — see `supportedOn`).
//
// ROLE FILTERING IS A RENDER-TIME CONCERN, NEVER A STORED ONE: a widget the
// viewer may not load is filtered out of both lists but stays untouched in their
// saved layout, so a role change (or being added to a widget's `ownerEmails`)
// restores it exactly as they left it.
//
// Pure (no React, no API) so it unit-tests standalone —
// see frontend/src/__tests__/dashboardWidgets.test.js.
// ============================================================

export type WidgetWidth = "full" | "half";
export type DashboardClient = "web" | "ios";

export interface DashboardWidgetMeta {
  id: string;
  title: string;
  description: string;
  defaultVisible: boolean;
  defaultWidth: WidgetWidth;
  bare?: boolean;
  adminOnly?: boolean;
  ownerEmails?: string[];
  /** Clients that render this widget. Absent = every client. A client not
   *  listed skips the id at render time; the saved layout is untouched. */
  clients?: DashboardClient[];
  /** Where a NEWLY SHIPPED widget lands in a layout saved before it existed:
   *  just before this widget id (when the saved layout has it), instead of at
   *  the end. Used only for a widget the saved layout has never seen — it
   *  never moves anything the person has already ordered. */
  insertBefore?: string;
}

export interface LayoutEntry {
  id: string;
  visible: boolean;
  width: WidgetWidth;
}

export interface Viewer {
  isAdmin?: boolean;
  email?: string | null;
  client?: DashboardClient;
}

export const WIDGET_WIDTHS: WidgetWidth[] = ['full', 'half'];

// Catalog order IS the default dashboard order.
export const DASHBOARD_WIDGETS: DashboardWidgetMeta[] = [
  {
    id: 'quickNote',
    title: 'Quick note',
    description: 'Jot a note without leaving the dashboard. Notes are private until you link them in Notes.',
    defaultVisible: true,
    defaultWidth: 'full',
    bare: true,
    // Sits above the Daily brief even on a dashboard customized before it
    // shipped (feedback #583).
    insertBefore: 'morningBrief',
    // Web only for now (frontend/src/components/dashboard/QuickNoteWidget.jsx);
    // iOS has its own Quick Note (app/quick-note.tsx) and skips this id.
    clients: ['web'],
  },
  {
    id: 'morningBrief',
    title: 'Daily brief',
    description: 'Your Harvi daily brief — what matters in your mail and calendar today.',
    // Default-visible, but the widget renders NOTHING for someone who has not
    // opted in to the brief and has never had one, so an un-customized
    // dashboard is unchanged for everyone else.
    defaultVisible: true,
    defaultWidth: 'full',
    bare: true,
    // iOS renders it too (mobile/src/components/DailyBriefCard.tsx), minus the
    // Inbox filing plan, which stays web-only.
  },
  {
    id: 'outstandingTasks',
    title: 'Outstanding tasks',
    description: 'Open tasks assigned to you, and the ones you have delegated out.',
    defaultVisible: true,
    defaultWidth: 'full',
    bare: true,
  },
  {
    id: 'capitalDeployment',
    title: 'Capital Deployment Verification',
    description:
      'Sage locations carrying a balance (debits − credits ≠ 0) that are not tagged ' +
      'to a deal or deal detail — capital deployed in the GL that no deal claims.',
    // Opt-in, not default-on: a fresh dashboard looks exactly as it did before
    // this widget shipped, and whoever can see it ticks it on in Customize.
    // `normalizeLayout` still appends it to every existing saved layout, so it
    // is always THERE to tick — just not shown until someone asks for it.
    defaultVisible: false,
    defaultWidth: 'full',
    // NAMED PEOPLE, NOT A ROLE. Company-wide GL money whose reconciliation is
    // two people's job, so the endpoint is gated on the authenticated email
    // (api/src/restrictedViewers.js) rather than on Admin. Mirrored here ONLY so
    // nobody else is shown a widget that would 401 — the API is the real gate,
    // exactly as `ownerEmail` in constants/nav.js is cosmetic for the Neil-only
    // pages. KEEP THIS LIST IN LOCKSTEP WITH CAPITAL_DEPLOYMENT_VIEWERS.
    ownerEmails: ['ngodfrey@harvus.com', 'kschneider@harvus.com'],
    bare: true,
    // Not built on iOS yet. A person who ticked it on in the web Customize
    // modal gets it there and simply does not see it on the phone.
    clients: ['web'],
  },
];

export const WIDGET_BY_ID: Record<string, DashboardWidgetMeta> =
  Object.fromEntries(DASHBOARD_WIDGETS.map(w => [w.id, w]));

// The layout a user who has never customized anything sees.
export const defaultLayout = (): LayoutEntry[] =>
  DASHBOARD_WIDGETS.map(w => ({
    id: w.id,
    visible: w.defaultVisible,
    width: w.defaultWidth,
  }));

// ------------------------------------------------------------------
// normalizeLayout(stored) → the ordered entry list to render/edit.
//
// Reconciles a saved layout against the current catalog:
//   • unknown ids (a removed/renamed widget) are dropped
//   • duplicate ids collapse to their first occurrence
//   • catalog widgets absent from the saved layout are APPENDED in catalog
//     order with their defaults, so a newly shipped widget shows up rather
//     than silently hiding behind an old saved layout
//   • a bad width falls back to the widget's default
//   • null/garbage input → the default layout
//
// Role filtering is deliberately NOT done here: an Admin-only widget stays in
// the stored layout for a non-admin so nothing is lost if their role changes.
// Rendering filters with `visibleWidgets`.
// ------------------------------------------------------------------
export function normalizeLayout(stored: unknown): LayoutEntry[] {
  const s = stored as { widgets?: unknown } | null | undefined;
  const entries: any[] | null = Array.isArray(s?.widgets) ? (s!.widgets as any[])
                : Array.isArray(stored)          ? (stored as any[])
                : null;
  if (!entries) return defaultLayout();

  const seen = new Set<string>();
  const out: LayoutEntry[] = [];
  for (const e of entries) {
    const meta = WIDGET_BY_ID[e?.id];
    if (!meta || seen.has(meta.id)) continue;
    seen.add(meta.id);
    out.push({
      id: meta.id,
      visible: e.visible === undefined ? meta.defaultVisible : !!e.visible,
      width: WIDGET_WIDTHS.includes(e.width) ? e.width : meta.defaultWidth,
    });
  }
  // Newly shipped widgets: append at the end (their catalog position is not
  // meaningful once the user has reordered things) — unless the widget names
  // one it belongs before (`insertBefore`) and the saved layout has that one.
  // Only widgets the saved layout has never seen are placed here, so the
  // person's own ordering of everything else is never changed.
  for (const w of DASHBOARD_WIDGETS) {
    if (seen.has(w.id)) continue;
    const entry = { id: w.id, visible: w.defaultVisible, width: w.defaultWidth };
    const at = w.insertBefore ? out.findIndex(e => e.id === w.insertBefore) : -1;
    if (at >= 0) out.splice(at, 0, entry);
    else out.push(entry);
  }
  return out;
}

// May this viewer load this widget at all? Two independent gates, and a widget
// may carry either: `adminOnly` (a role) and `ownerEmails` (named people).
//
// ABSENCE OF AN EMAIL IS NOT A MATCH. A viewer whose identity has not resolved
// yet must fail an `ownerEmails` check rather than pass it — the alternative is
// flashing a restricted widget during sign-in.
function permitted(meta: DashboardWidgetMeta, { isAdmin, email, client }: Viewer): boolean {
  if (meta.adminOnly && !isAdmin) return false;
  if (meta.ownerEmails) {
    const e = String(email || '').trim().toLowerCase();
    if (!e || !meta.ownerEmails.some(o => o.trim().toLowerCase() === e)) return false;
  }
  if (client && meta.clients && !meta.clients.includes(client)) return false;
  return true;
}

// The entries actually rendered, in order: visible, and permitted for the viewer.
export function visibleWidgets(
  layout: LayoutEntry[] | null | undefined,
  { isAdmin = false, email = '', client }: Viewer = {},
): LayoutEntry[] {
  return (layout || []).filter(e => {
    const meta = WIDGET_BY_ID[e.id];
    if (!meta || !e.visible) return false;
    return permitted(meta, { isAdmin, email, client });
  });
}

// The entries offered in the customize UI — same filter, visible or not, so
// nobody is shown a toggle for a widget they cannot load.
export function editableWidgets(
  layout: LayoutEntry[] | null | undefined,
  { isAdmin = false, email = '', client }: Viewer = {},
): LayoutEntry[] {
  return (layout || []).filter(e => {
    const meta = WIDGET_BY_ID[e.id];
    if (!meta) return false;
    return permitted(meta, { isAdmin, email, client });
  });
}

// Move the entry at `index` by `delta` positions, returning a new array.
// Out-of-range moves are no-ops (returns the same array) so the caller can
// disable a button by identity comparison if it wants.
export function moveWidget(layout: LayoutEntry[], index: number, delta: number): LayoutEntry[] {
  const target = index + delta;
  if (!Array.isArray(layout)) return layout;
  if (index < 0 || index >= layout.length) return layout;
  if (target < 0 || target >= layout.length) return layout;
  const next = layout.slice();
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  return next;
}

// Move the entry with id `dragId` to sit where `overId` currently is (the
// drag-and-drop reorder). Returns a new array; a no-op returns the input.
export function reorderWidget(layout: LayoutEntry[], dragId: string, overId: string): LayoutEntry[] {
  if (!Array.isArray(layout) || dragId === overId) return layout;
  const from = layout.findIndex(e => e.id === dragId);
  const to   = layout.findIndex(e => e.id === overId);
  if (from < 0 || to < 0) return layout;
  return moveWidget(layout, from, to - from);
}

// Patch one entry (visibility / width), returning a new array.
export function setWidget(
  layout: LayoutEntry[] | null | undefined, id: string, patch: Partial<LayoutEntry>,
): LayoutEntry[] {
  return (layout || []).map(e => (e.id === id ? { ...e, ...patch } : e));
}

// The body PUT /api/dashboard/layout expects.
export const toLayoutBody = (layout: LayoutEntry[] | null | undefined) => ({
  widgets: (layout || []).map(({ id, visible, width }) => ({ id, visible, width })),
});

// Do two layouts describe the same dashboard? Used to skip a pointless save.
export function layoutsEqual(a: unknown, b: unknown): boolean {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return (a as LayoutEntry[]).every((e, i) =>
    e.id === (b as LayoutEntry[])[i].id && !!e.visible === !!(b as LayoutEntry[])[i].visible && e.width === (b as LayoutEntry[])[i].width);
}
